import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { roles } from '../../utils/roles';
import { LocalizationProvider, DesktopDatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { abortController } from '../../utils/abortController';
import { BiReset } from 'react-icons/bi';
import withAuth from '../../lib/withAuth';
import useUser from '../../hooks/useUser';
import useSessionExpire from '../../hooks/useSessionExpire';
import Button from '@mui/material/Button';
import Backdrop from '@mui/material/Backdrop';
import Grid from '@mui/material/Grid';
import ButtonGroup from '@mui/material/ButtonGroup';
import Loader from '../../components/Loader';
import Alert from '../../components/Alert';
import dayjs from 'dayjs';
import axios from 'axios';
import MaterialReactDataTable from '../../components/MaterialReactDataTable';
import VirtualizedAutocomplete from '../../components/Autocomplete';

const Settings = ({ logOut = () => {} }) => 
{
  const [isLoading, setIsLoading] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState("");
  const location = useLocation();
  const { pathname } = useLocation();

  /* SESSION EXPIRY VARIABLES */
  const triggerSessionExpire = useSessionExpire();

  /* AUTHENTICATION VARIABLES */
  const currentUser = useUser();

  /* DATE VARIABLES */
  const [startDate, setStartDate] = useState(dayjs().startOf("month").format("MM/DD/YYYY"));
  const [endDate, setEndDate] = useState(dayjs().format("MM/DD/YYYY"));
  const [lockDate, setLockDate] = useState(null);

  /* LIST VARIABLES */
  const [users, setUsers] = useState([]);

  /* SELECTED LIST VARIABLES */
  const [selectedUser, setSelectedUser] = useState(null);

  /* CONDITIONAL FILTER VARIABLES */
  const [isFilterable, setIsFilterable] = useState(true);
  const [filter, setFilter] = useState(true);
  const [isFiltered, setIsFiltered] = useState(true);
  
  /* DATATABLE VARIABLES */
  const [periodLockData, setPeriodLockData] = useState([]);
  const [isDataChanged, setIsDataChanged] = useState(false);
  const [toggleDisplayData, setToggleDisplayData] = useState(false);
  const tableRef = useRef();

  /* DATATABLE GLOBAL FILTER VARIABLES */
  const [globalFilterValue, setGlobalFilterValue] = useState("");
  
  useEffect(() =>
    {
        const fetchLists = async () => 
        {
            axios({
                method: "post",
                url: "/api/GetUsersList",
                data: { pathname },
                signal: abortController.signal
            })
            .then((response) => 
            {
                const { status, data } = response;
        
                if (status === 200) 
                {
                    const users = data?.users || [];
                    setUsers(users);
                }
            })
            .catch((error) => 
            {
                console.log("Get User List Api: ", error);

                if (axios.isCancel(error) || error.code === "ERR_CANCELED") 
                {
                    return;
                }

                const status = error?.response?.status;
        
                if (status === 403) 
                {
                    triggerSessionExpire();
                } 
                else 
                {
                    setAlertMessage(
                        status === 401
                            ? "Unauthorized access. You do not have the required permissions to perform this action."
                            : status === 429
                            ? "Request limit exceeded. Your account is temporarily disabled. Please contact the site administrator."
                            : "An error occurred while processing your request. Please try again later or contact the site administrator."
                    );
                    setAlertType("error");
                    setShowAlert(true);
                
                    if (status === 429) 
                    {
                        setTimeout(logOut, 3000);
                    }
                }
            });
        }

        fetchLists();

        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, []);

  useEffect(() => 
  {
    if (selectedUser?.id || startDate || endDate)
    {
      setIsFilterable(true);
    }
    else
    {
      setIsFilterable(false);
    }

  }, [selectedUser?.id, startDate, endDate]);

  

  useEffect(() => 
  {
    const fetchData = async () => 
    {
      setIsLoading(true);
      setShowAlert(false);
      setAlertMessage("");
      setAlertType("");

      await axios({
        method: "post",
        url: "/api/GetAllPeriodLockData",
        data: {
          user_id: selectedUser?.id,
          start_date: startDate,
          end_date: endDate,
          filter_value: globalFilterValue
        },
        signal: abortController.signal
      })
      .then((response) => 
      {
        setIsLoading(false);
        const { status, data } = response;

        if (status === 200) 
        {
          const { period_lock_data: periodLockData = []} = data || {};

          if (filter && periodLockData.length === 0)
          {
            setAlertMessage("No data found that matches the selected filter options.");
            setAlertType("info");
            setShowAlert(true);
          }

          setPeriodLockData(periodLockData);
        }
        else 
        {
          setAlertMessage("An error occurred while processing your request. Please try again later or contact the site administrator.");
          setAlertType("error");
          setShowAlert(true);
        }
      })
      .catch((error) => 
      {
        console.error("Get All PeriodLock Data Api: ", error);
        setIsLoading(false);

        if (axios.isCancel(error) || error.code === "ERR_CANCELED") 
        {
          return;
        }

        const status = error?.response?.status;

        if (status === 403) 
        {
          triggerSessionExpire();
        }
        else 
        {
          setAlertMessage(
            status === 401
              ? "Unauthorized access. You do not have the required permissions to perform this action."
              : status === 429
              ? "Request limit exceeded. Your account is temporarily disabled. Please contact the site administrator."
              : "An error occurred while processing your request. Please try again later or contact the site administrator."
          );
          setAlertType("error");
          setShowAlert(true);

          if (status === 429) 
          {
            setTimeout(logOut, 3000);
          }
        }
      });
    }

    fetchData();

    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [filter, toggleDisplayData]);

  const convertToDayJsObject = (date) => 
  {
    const convertedDate = date ? dayjs(date, "MM/DD/YYYY") : null;
    return convertedDate;
  }

  const handleDateChange = (newDate, type) => 
  {
    const date = newDate ? dayjs(newDate).format("MM/DD/YYYY") : newDate;
    type === "Start Date" ? setStartDate(date) : type === "End Date" ? setEndDate(date) : setLockDate(date);
  }

  const handleSave = async () => 
    {

        setIsLoading(true);
        setShowAlert(false);
        setAlertMessage("");
        setAlertType("");

        await axios({
        method: "post",
        url: "/api/SetPeriodLockDate",
        data: {
            pathname: location?.pathname,
            period_lock_date: lockDate,
            user_id: currentUser?.id
        },
        signal: abortController.signal
        })
        .then((response) => 
        {
        setIsLoading(false);
        const { status, data } = response;

        if (status === 200) 
        {
            setAlertMessage(data?.message);
            setAlertType("success");
            setShowAlert(true);
            setToggleDisplayData(!toggleDisplayData)
        }
        else 
        {
            setAlertMessage("An error occurred while processing your request. Please try again later or contact the site administrator.");
            setAlertType("error");
            setShowAlert(true);
        }
        })
        .catch((error) => 
        {
        console.log("Filter Table Data Api: ", error);
        setIsLoading(false);

        if (axios.isCancel(error) || error.code === "ERR_CANCELED") 
        {
            return;
        }

        const status = error?.response?.status;

        if (status === 403) 
        {
            triggerSessionExpire();
        }
        else 
        {
            setAlertMessage(
            status === 401
                ? "Unauthorized access. You do not have the required permissions to perform this action."
                : status === 429
                ? "Request limit exceeded. Your account is temporarily disabled. Please contact the site administrator."
                : "An error occurred while processing your request. Please try again later or contact the site administrator."
            );
            setAlertType("error");
            setShowAlert(true);

            if (status === 429) 
            {
            setTimeout(logOut, 3000);
            }
        }
        });
  }

  const handleFilter = () => 
  {
      setIsFiltered(true);
      setFilter(true);
      setToggleDisplayData(!toggleDisplayData);
  }

  const handleReset = () => 
  {
    setSelectedUser(null);
    setStartDate(null);
    setEndDate(null);
    setIsFilterable(false);
    setFilter(false);
    setIsFiltered(false);
    setToggleDisplayData(!toggleDisplayData);
  }

  const handleGlobalFilter = async (rowData) => 
  {

    setIsLoading(true);
    setShowAlert(false);
    setAlertMessage("");
    setAlertType("");

    await axios({
      method: "post",
      url: "/api/FilterTableData",
      data: {
        pathname: location?.pathname,
        table_data: rowData,
        filter_value: globalFilterValue
      },
      signal: abortController.signal
    })
    .then((response) => 
    {
      setIsLoading(false);
      const { status, data } = response;

      if (status === 200) 
      {
        tableRef?.current?.setFilteredData(data || []);
      }
      else 
      {
        setAlertMessage("An error occurred while processing your request. Please try again later or contact the site administrator.");
        setAlertType("error");
        setShowAlert(true);
      }
    })
    .catch((error) => 
    {
      console.log("Filter Table Data Api: ", error);
      setIsLoading(false);

      if (axios.isCancel(error) || error.code === "ERR_CANCELED") 
      {
        return;
      }

      const status = error?.response?.status;

      if (status === 403) 
      {
        triggerSessionExpire();
      }
      else 
      {
        setAlertMessage(
          status === 401
            ? "Unauthorized access. You do not have the required permissions to perform this action."
            : status === 429
            ? "Request limit exceeded. Your account is temporarily disabled. Please contact the site administrator."
            : "An error occurred while processing your request. Please try again later or contact the site administrator."
        );
        setAlertType("error");
        setShowAlert(true);

        if (status === 429) 
        {
          setTimeout(logOut, 3000);
        }
      }
    });
  }

  return (
    <div className = "content-container d-flex flex-column container mass-update position-relative">
        <Alert
            show = {showAlert}
            message = {alertMessage}
            type = {alertType}
            setShow = {setShowAlert}
        />

        <Backdrop
            sx = {{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
            open = {isLoading}
        >
            <Loader/>
        </Backdrop>

        <h4 className = "page-heading m-0">
            Settings
        </h4>

        <Grid container spacing = {1} justifyContent = "start" alignItems = "center">
          <Grid size = {{ xs: 2 }}>
            <LocalizationProvider dateAdapter = {AdapterDayjs}>
              <DesktopDatePicker
              inputFormat = "DD-MMM-YYYY"
              label = "Lock Date"
              className = "date-picker w-100"
              value = {convertToDayJsObject(lockDate)}
              onChange = {(newDate) => handleDateChange(newDate)}
              maxDate={dayjs().subtract(1,'day')}
              slotProps = {{
                  actionBar: {
                  actions: ["clear"]
                  },
                  textField: {
                  InputProps: {
                      size: "small",
                      disabled: true
                  }
                  }
              }}
              />
            </LocalizationProvider>
          </Grid>
  
          <Grid size = {{ xs: 2 }}>
              <Button type = "submit" className = "filter-button" variant = "contained" disabled = {!lockDate} color = "primary" onClick = {handleSave}>Save</Button>
          </Grid>
        </Grid>

        <hr className='mt-0'/>
        
        <Grid container spacing = {1} justifyContent = "center" alignItems = "center">

             <Grid size = {{ xs: 2 }} className = "audit-trail-filter autocomplete">
                <VirtualizedAutocomplete
                    isMultiple = {false}
                    isObject = {true}
                    isWritable = {true}
                    filterOn = "User"
                    options = {users}
                    selectedOptions = {selectedUser}
                    handleSelectChange = {(filterOn, newValue) => setSelectedUser(newValue)}
                />
            </Grid>

            <Grid size = {{ xs: 2 }}>
                <LocalizationProvider dateAdapter = {AdapterDayjs}>
                    <DesktopDatePicker
                    inputFormat = "DD-MMM-YYYY"
                    label = "Start Date"
                    className = "date-picker w-100"
                    maxDate = {convertToDayJsObject(endDate)}
                    value = {convertToDayJsObject(startDate)}
                    onChange = {(newDate) => handleDateChange(newDate, "Start Date")}
                    onAccept = {(newDate) => handleDateChange(newDate, "Start Date")}
                    slotProps = {{
                        actionBar: {
                        actions: ["clear"]
                        },
                        textField: {
                        InputProps: {
                            size: "small",
                            disabled: true
                        }
                        }
                    }}
                    />
                </LocalizationProvider>
            </Grid>

            <Grid size = {{ xs: 2 }}>
                <LocalizationProvider dateAdapter = {AdapterDayjs}>
                    <DesktopDatePicker
                    inputFormat = "DD-MMM-YYYY"
                    label = "End Date"
                    className = "date-picker w-100"
                    minDate = {convertToDayJsObject(startDate)}
                    value = {convertToDayJsObject(endDate)}
                    onChange = {(newDate) => handleDateChange(newDate, "End Date")}
                    onAccept = {(newDate) => handleDateChange(newDate, "End Date")}
                    slotProps = {{
                        actionBar: {
                        actions: ["clear"]
                        },
                        textField: {
                        InputProps: {
                            size: "small",
                            disabled: true
                        }
                        }
                    }}
                    />
                </LocalizationProvider>
            </Grid>

            <Grid size = {{ xs: 2 }}>
                <ButtonGroup variant = "contained" disableElevation = {true}>
                    <Button type = "submit" className = "filter-button" color = "primary" disabled = {!isFilterable} onClick = {handleFilter}>Filter</Button>
                    <Button className = "reset-button" color = "info" disabled = {!isFiltered} startIcon = {<BiReset />} onClick = {handleReset}></Button>
                </ButtonGroup>
            </Grid>
        </Grid>

        <LocalizationProvider dateAdapter = {AdapterDayjs}>
            <MaterialReactDataTable
                ref = {tableRef}
                isLoading = {isLoading}
                isDataChanged = {isDataChanged}
                globalFilterValue = {globalFilterValue}
                tableData = {periodLockData}
                convertToDayJsObject = {convertToDayJsObject}
                setGlobalFilterValue = {setGlobalFilterValue}
                setIsDataChanged = {setIsDataChanged}
                handleGlobalFilter = {handleGlobalFilter}
                showActions = {false}
            />
        </LocalizationProvider>
    </div>
  );
}

export default withAuth(Settings)([
  roles[8]
]);